import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { useKioskSession } from '@/hooks/kiosk';
import { toast } from '@/hooks/ui/use-toast';
import { Camera, Loader2, AlertCircle, CheckCircle2, Keyboard, Undo2 } from 'lucide-react';
import QRCodeScanner from '@/components/QRCodeScanner';
import PassUpdateForm from '@/components/PassUpdateForm';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/core/utils';

interface CustomerLoyaltyInfo {
  customer_id: string;
  customer_name?: string;
  program_name: string;
  program_type: 'points' | 'stamps';
  current_points: number;
  current_stamps: number;
  total_visits: number;
  business_id: string;
  loyalty_program_id?: string;
  wallet_type?: 'apple' | 'google';
}

interface CouponInfo {
  type: 'coupon';
  coupon_offer_id: string;
  customer_id: string;
  coupon_name: string;
  coupon_header: string;
  coupon_description: string;
  discount_type: string;
  discount_value: number;
  minimum_purchase: number;
  business_id: string;
  business_name: string;
  pass_id: string;
  pass_uuid?: string;
  google_wallet_object_id?: string;
  platform: 'apple_wallet' | 'google_wallet';
  valid_until?: string;
  can_redeem: boolean;
  already_redeemed: boolean;
  redemption_date?: string;
}

type PassInfo = CustomerLoyaltyInfo | CouponInfo;

interface CustomerScannerModeProps {
  className?: string;
}

/**
 * Customer scanner mode for loyalty programs and coupons
 * Extracted from UpdatePassPage for use in unified kiosk
 */
export function CustomerScannerMode({ className }: CustomerScannerModeProps) {
  const { session, businessId } = useKioskSession();
  const [scanning, setScanning] = useState(false);
  const [customerInfo, setCustomerInfo] = useState<PassInfo | null>(null);
  const [originalCustomerInfo, setOriginalCustomerInfo] = useState<PassInfo | null>(null);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [manualEntry, setManualEntry] = useState(false);
  const [manualQRData, setManualQRData] = useState('');
  const [lastProcessedQR, setLastProcessedQR] = useState<string | null>(null);
  const [currentPassId, setCurrentPassId] = useState<string | null>(null);
  const [undoCountdown, setUndoCountdown] = useState<number | null>(null);
  const [pendingPoints, setPendingPoints] = useState(0);
  const [pendingStamps, setPendingStamps] = useState(0);

  const undoIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const undoTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasAutoStartedRef = useRef<boolean>(false);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (undoIntervalRef.current) clearInterval(undoIntervalRef.current);
      if (undoTimeoutRef.current) clearTimeout(undoTimeoutRef.current);
    };
  }, []);

  const startScanning = useCallback(async () => {
    const isSecureContext = window.location.protocol === 'https:' || window.location.hostname === 'localhost';

    if (!isSecureContext) {
      setError("Camera access requires HTTPS. Please use manual entry.");
      return;
    }

    setScanning(true);
    setError(null);
    setSuccess(null);
    setManualEntry(false);
    setLastProcessedQR(null);

    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error("Camera not supported in this browser");
      }
    } catch (err) {
      setError("Failed to start camera. Please use manual entry.");
      setScanning(false);
    }
  }, []);

  // Auto-start scanning
  useEffect(() => {
    if (session && !hasAutoStartedRef.current) {
      hasAutoStartedRef.current = true;
      setTimeout(() => startScanning(), 500);
    }
  }, [session, startScanning]);

  const validateAndGetCustomerInfo = async (passId: string) => {
    if (!businessId) {
      setError("Business ID not found. Please re-login.");
      return;
    }

    setUpdating(true);
    setError(null);

    try {
      const response = await supabase.functions.invoke('validate-pass', {
        body: { pass_identifier: passId },
      });

      if (response.error) {
        throw new Error(response.error.message || 'Failed to validate pass');
      }

      const passData = response.data;

      if (!passData) {
        throw new Error('Pass not found');
      }

      // Check business access
      if (passData.business_id !== businessId) {
        throw new Error('This pass belongs to a different business');
      }

      setCurrentPassId(passId);
      setCustomerInfo(passData);
      setOriginalCustomerInfo(JSON.parse(JSON.stringify(passData)));
      setPendingPoints(0);
      setPendingStamps(0);

      // Haptic feedback
      if ('vibrate' in navigator) {
        navigator.vibrate(100);
      }

    } catch (err: any) {
      setError(err.message || 'Failed to validate pass');
      toast.error('Validation Failed', { description: err.message });
    } finally {
      setUpdating(false);
    }
  };

  const handleQRCodeDetected = async (qrData: string) => {
    if (lastProcessedQR === qrData) return;

    setScanning(false);
    setLastProcessedQR(qrData);
    const passId = qrData.trim();
    await validateAndGetCustomerInfo(passId);
  };

  const handleManualSubmit = () => {
    if (manualQRData.trim()) {
      handleQRCodeDetected(manualQRData.trim());
      setManualEntry(false);
    }
  };

  const startUndoCountdown = () => {
    if (undoIntervalRef.current) clearInterval(undoIntervalRef.current);
    if (undoTimeoutRef.current) clearTimeout(undoTimeoutRef.current);

    setUndoCountdown(10);

    undoIntervalRef.current = setInterval(() => {
      setUndoCountdown((prev) => {
        if (prev === null || prev <= 0.1) {
          return 0;
        }
        return prev - 0.1;
      });
    }, 100);

    undoTimeoutRef.current = setTimeout(() => {
      if (undoIntervalRef.current) clearInterval(undoIntervalRef.current);
      setUndoCountdown(null);
      finalizeTransaction();
    }, 10000);
  };

  const handleUndo = () => {
    setUndoCountdown(null);
    if (undoIntervalRef.current) clearInterval(undoIntervalRef.current);
    if (undoTimeoutRef.current) clearTimeout(undoTimeoutRef.current);

    setCustomerInfo(JSON.parse(JSON.stringify(originalCustomerInfo)));
    setPendingPoints(0);
    setPendingStamps(0);
    setSuccess('Restored to original state');

    setTimeout(() => setSuccess(null), 3000);
  };

  const finalizeTransaction = async () => {
    if (!customerInfo || (pendingPoints === 0 && pendingStamps === 0)) {
      handleNextCustomer();
      return;
    }

    try {
      // Call loyalty transaction endpoint
      const response = await fetch(
        'https://pass.taginn.app/loyalty/transaction',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            customer_id: (customerInfo as CustomerLoyaltyInfo).customer_id,
            program_id: (customerInfo as CustomerLoyaltyInfo).loyalty_program_id,
            transaction_type: 'earn',
            stamps_to_earn: pendingStamps > 0 ? pendingStamps : null,
            direct_points: pendingPoints > 0 ? pendingPoints : null,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Transaction failed');
      }

      toast.success('Transaction Saved!');

      // Send wallet update notification
      if ((customerInfo as CustomerLoyaltyInfo).wallet_type === 'apple') {
        fetch('https://pass.taginn.app/apple-wallet/send-pass-update-notification', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ passId: currentPassId }),
        }).catch(console.error);
      }

    } catch (err) {
      console.error('Finalize error:', err);
      toast.error('Failed to save transaction');
    }

    handleNextCustomer();
  };

  const handleNextCustomer = () => {
    setCustomerInfo(null);
    setOriginalCustomerInfo(null);
    setCurrentPassId(null);
    setLastProcessedQR(null);
    setPendingPoints(0);
    setPendingStamps(0);
    setUndoCountdown(null);
    setSuccess(null);
    setError(null);
    startScanning();
  };

  const handleUpdatePass = async (action: string, value?: number) => {
    if (!customerInfo) return;

    if (action === 'add_points' && value) {
      setPendingPoints((prev) => prev + value);
      setCustomerInfo((prev) => {
        if (!prev || 'type' in prev) return prev;
        return { ...prev, current_points: prev.current_points + value };
      });
      startUndoCountdown();
      toast.success(`+${value} points added`);
    } else if (action === 'add_stamps' && value) {
      setPendingStamps((prev) => prev + value);
      setCustomerInfo((prev) => {
        if (!prev || 'type' in prev) return prev;
        return { ...prev, current_stamps: prev.current_stamps + value };
      });
      startUndoCountdown();
      toast.success(`+${value} stamps added`);
    } else if (action === 'redeem_coupon') {
      // Handle coupon redemption
      const couponInfo = customerInfo as CouponInfo;
      try {
        const response = await supabase.functions.invoke('redeem-coupon', {
          body: {
            coupon_offer_id: couponInfo.coupon_offer_id,
            customer_id: couponInfo.customer_id,
            google_wallet_object_id: couponInfo.google_wallet_object_id,
            platform: couponInfo.platform,
            redemption_method: 'scanner',
          },
        });

        if (response.error) throw response.error;

        toast.success('Coupon Redeemed!');

        // Send Apple wallet notification
        if (couponInfo.platform === 'apple_wallet' && couponInfo.pass_uuid) {
          fetch('https://pass.taginn.app/apple-wallet/send-pass-update-notification', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ passId: couponInfo.pass_uuid }),
          }).catch(console.error);
        }

        setTimeout(handleNextCustomer, 1500);
      } catch (err) {
        toast.error('Failed to redeem coupon');
      }
    }
  };

  return (
    <div className={cn('flex flex-col flex-1 p-6 bg-gray-50 dark:bg-background', className)}>
      {/* Scanning state */}
      {scanning && !customerInfo && (
        <div className="flex-1 flex flex-col items-center justify-center">
          <div className="w-full max-w-md mb-4">
            <QRCodeScanner
              onScan={handleQRCodeDetected}
              onError={(err) => {
                setError(err);
                setScanning(false);
              }}
              onClose={() => setScanning(false)}
              isActive={scanning}
              scanType="both"
            />
          </div>

          <Button
            variant="outline"
            onClick={() => {
              setScanning(false);
              setManualEntry(true);
            }}
            className="mt-4"
          >
            <Keyboard className="w-4 h-4 mr-2" />
            Manual Entry
          </Button>
        </div>
      )}

      {/* Manual entry */}
      {manualEntry && !customerInfo && (
        <div className="flex-1 flex flex-col items-center justify-center">
          <Card className="w-full max-w-md">
            <CardContent className="space-y-4 pt-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 text-center mb-4">
                Enter Pass ID Manually
              </h2>

              <Input
                placeholder="Enter pass ID..."
                value={manualQRData}
                onChange={(e) => setManualQRData(e.target.value)}
                className="text-center text-lg"
                autoFocus
              />

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setManualEntry(false);
                    startScanning();
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button onClick={handleManualSubmit} className="flex-1">
                  Submit
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Loading state */}
      {updating && (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">Validating pass...</p>
          </div>
        </div>
      )}

      {/* Error state */}
      {error && !scanning && !updating && (
        <div className="flex-1 flex flex-col items-center justify-center">
          <Alert variant="destructive" className="max-w-md mb-4">
            <AlertCircle className="w-4 h-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>

          <Button onClick={startScanning}>
            <Camera className="w-4 h-4 mr-2" />
            Try Again
          </Button>
        </div>
      )}

      {/* Customer info / update form */}
      {customerInfo && !updating && (
        <div className="flex-1 flex flex-col max-w-lg mx-auto w-full">
          {/* Undo bar */}
          {undoCountdown !== null && (
            <div className="mb-4 p-4 bg-orange-50 dark:bg-orange-500/10 border border-orange-200 dark:border-orange-500/40 rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <span className="text-orange-700 dark:text-orange-400 font-medium">
                  Changes pending...
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleUndo}
                  className="text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300"
                >
                  <Undo2 className="w-4 h-4 mr-1" />
                  Undo
                </Button>
              </div>
              <div className="w-full bg-orange-100 dark:bg-orange-900/50 rounded-full h-2">
                <div
                  className="bg-orange-500 h-2 rounded-full transition-all duration-100"
                  style={{ width: `${(undoCountdown / 10) * 100}%` }}
                />
              </div>
            </div>
          )}

          {/* Success message */}
          {success && (
            <Alert className="mb-4 bg-green-50 dark:bg-green-500/10 border-green-200 dark:border-green-500/40">
              <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400" />
              <AlertDescription className="text-green-700 dark:text-green-400">
                {success}
              </AlertDescription>
            </Alert>
          )}

          {/* Pass update form */}
          <PassUpdateForm
            customerInfo={customerInfo}
            onUpdate={handleUpdatePass}
            onCancel={handleNextCustomer}
            isUpdating={updating}
          />

          {/* Next customer button */}
          <Button
            onClick={() => {
              if (undoCountdown !== null) {
                finalizeTransaction();
              } else {
                handleNextCustomer();
              }
            }}
            className="mt-4 w-full h-14 text-lg"
            variant="outline"
          >
            {undoCountdown !== null ? 'Save & Next Customer' : 'Scan Next Customer'}
          </Button>
        </div>
      )}

      {/* Idle state */}
      {!scanning && !manualEntry && !customerInfo && !updating && !error && (
        <div className="flex-1 flex flex-col items-center justify-center">
          <Button
            onClick={startScanning}
            size="lg"
            className="h-20 px-12 text-xl"
          >
            <Camera className="w-6 h-6 mr-3" />
            Start Scanning
          </Button>
        </div>
      )}
    </div>
  );
}
